import { Injectable } from '@angular/core';
import {
  InterestedDriver,
  Journey,
  Reservation,
  JourneyInterestedDriver,
  RideRequest,
  ReservationService,
  JourneyService,
  ProviderSelectionDetail,
  Driver,
  CustomerServiceNote
} from '@uzurv/clara';
import { Observable } from 'rxjs';
import { ReservationListItemVm } from '../../components/reservation';
import { isNullOrUndefined, ObjectConstants, PairingException } from '@uzurv/core';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class TripService {
  constructor(
    private dialog: MatDialog,
    private journeyService: JourneyService,
    private reservationService: ReservationService,
  ) {}

  selectDriver(
    interestedDriver: InterestedDriver | JourneyInterestedDriver,
    trip: ReservationListItemVm
  ): Observable<Reservation | Journey> {
    let restOperation: Observable<Reservation | Journey>;

    if (interestedDriver instanceof InterestedDriver) {
      const reservation = new Reservation();
      reservation.driver = interestedDriver.driver;
      reservation.rideRequest = new RideRequest();
      reservation.rideRequest.id = trip.rideRequestDetail.id;

      if (interestedDriver.proposingNewPickupTime) {
        restOperation = this.reservationService.createReservationWithProposedPickupTime(reservation, interestedDriver.id);
      } else {
        restOperation = this.reservationService.create(reservation);
      }
    } else if (interestedDriver instanceof JourneyInterestedDriver) {
      const journeyInterestedDriver: JourneyInterestedDriver = interestedDriver;

      restOperation = this.journeyService.manuallyPairJourneyWithDriver(
        journeyInterestedDriver.journeyId,
        journeyInterestedDriver.driver.id
      );
    }

    return restOperation;
  }

  selectProvider(providerSelectionDetail: ProviderSelectionDetail, trip: ReservationListItemVm): Observable<Reservation> {
    let restOperation: Observable<Reservation>;
    let interestedDriver = new InterestedDriver();
    const reservation = new Reservation();

    if (!isNullOrUndefined(providerSelectionDetail?.interestedDriver)) {
      interestedDriver = providerSelectionDetail.interestedDriver;

      reservation.driver = interestedDriver.driver;

      reservation.rideRequest = new RideRequest();
      reservation.rideRequest.id = trip.rideRequestDetail.id;

      if (interestedDriver.proposingNewPickupTime) {
        restOperation = this.reservationService.createReservationWithProposedPickupTime(reservation, interestedDriver.id);
      } else {
        restOperation = this.reservationService.create(reservation);
      }
    } else {
      reservation.driver = new Driver();
      reservation.driver.id = providerSelectionDetail.provider.proxyDriverId;

      reservation.rideRequest = new RideRequest();
      reservation.rideRequest.id = trip.rideRequestDetail.id;

      restOperation = this.reservationService.create(reservation);
    }

    return restOperation;
  }

  handleSelectionErrorMessages(message: string): PairingException {
    let errMessage = message;
    let refresh = false;

    if (message.includes(`The Reservation overlaps one of Driver's existing Reservations`)) {
      errMessage = 'The driver you chose for this ride request is no longer available. Please choose a different driver.';
    } else if (message.includes('A reservation already exists for the RideRequest')) {
      errMessage = 'A reservation already exists for this ride request.';
    } else if (message.includes('Specified Driver does not have the correct role')) {
      errMessage = 'The driver you chose for this ride request is no longer available. Please choose a different driver.';
    } else if (message.includes('did not express interest in the RideRequest')) {
      errMessage = 'The driver you chose for this ride request is no longer available. Please choose a different driver.';
      refresh = true;
    } else if (message.includes('The Reservation could not be saved. Driver Payment preauth result:')) {
      errMessage = 'The driver you chose for this ride request is no longer available. Please choose a different driver.';
    } else if (message.includes('The Reservation could not be saved. Wrong PaymentAccountStatus for driver PaymentAccount')) {
      errMessage = 'The driver you chose for this ride request is no longer available. Please choose a different driver.';
    } else if (message.includes('The RideRequest could not be saved. Wrong PaymentAccountStatus for rider PaymentAccount')) {
      // TODO: The CSR should be given the option to choose a new payment method. See DEV-442.
      errMessage = 'The payment account associated with this ride request is no longer valid. Please select a different one.';
    } else if (message.includes('nested exception')) {
      // May occur when multiple drivers are interested, one withdraws, and another is selected for the request.
      errMessage = 'Something went wrong. Please refresh';
      refresh = true;
    }

    return new PairingException(message, refresh);
  }

  assignTripToCustomerServiceNote(customerServiceNote: CustomerServiceNote, trip: ReservationListItemVm): CustomerServiceNote {
    if (!trip.driver) {
      customerServiceNote.objectName = ObjectConstants.RIDE_REQUEST;
      customerServiceNote.objectId = trip.rideRequestDetail.id;
    } else {
      customerServiceNote.objectName = ObjectConstants.RESERVATION;
      customerServiceNote.objectId = trip.id;
    }

    return customerServiceNote;
  }
}
